import { WebGLRenderer, NoToneMapping, LinearFilter, LinearMipmapLinearFilter, SRGBColorSpace } from 'three'
import VaultHero from './VaultHero.js'

export default class Renderer {
    constructor(_options = {}) {
        this.experience = new VaultHero()
        this.config = this.experience.config
        this.time = this.experience.time
        this.sizes = this.experience.sizes
        this.scene = this.experience.scene
        this.camera = this.experience.camera

        // Frame limiting setup - 120fps means ~8.33ms per frame
        this.frameInterval = 1000 / 120
        this.timeSinceLastFrame = 0

        this.setInstance()
    }

    setInstance() {
        this.instance = new WebGLRenderer({
            alpha: true,
            antialias: false,
            powerPreference: 'high-performance',
            depth: false,
            stencil: false,
            preserveDrawingBuffer: false,
            failIfMajorPerformanceCaveat: true
        })
        
        // Set size and pixel ratio
        this.instance.setSize(this.config.width, this.config.height)
        this.instance.setPixelRatio(Math.min(window.devicePixelRatio, 2))

        // Configure for best texture quality
        this.instance.outputColorSpace = SRGBColorSpace

        // Get context for texture filtering
        this.context = this.instance.getContext()
        
        // Enable texture optimizations
        if (this.context) {
            // Enable anisotropic filtering
            const ext = this.context.getExtension('EXT_texture_filter_anisotropic')
            if (ext) {
                this.maxAnisotropy = Math.min(
                    this.context.getParameter(ext.MAX_TEXTURE_MAX_ANISOTROPY_EXT),
                    16 // Cap at 16x for performance
                )
            }

            // Enable floating point textures if available
            this.context.getExtension('OES_texture_float')
            this.context.getExtension('OES_texture_float_linear')
        }

        // Disable features we don't need
        this.instance.shadowMap.enabled = false
        this.instance.physicallyCorrectLights = false
    }

    resize() {
        this.instance.setSize(this.config.width, this.config.height)
        this.instance.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        this.update()
    }

    update() {
        this.timeSinceLastFrame += this.time.delta

        if (this.timeSinceLastFrame >= this.frameInterval) {
            this.timeSinceLastFrame = 0
            this.instance.render(this.scene, this.camera.instance)
        }
    }

    destroy() {
        this.instance.renderLists.dispose()
        this.instance.dispose()
        this.instance.forceContextLoss()
        this.context = null
        this.instance = null
    }
}
